import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, output } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputMask } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { RadioButton } from 'primeng/radiobutton';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { PesquisarPessoaCustomComponent } from '@/shared/components/pessoa/pesquisar-pessoa-custom/pesquisar-pessoa-custom';
import { HeaderDivisor } from '@/shared/components/layout/header-divisor/header-divisor';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type TipoPessoaPesquisa = 'MEDICO' | 'PRESTADOR' | 'OUTROSPF' | 'OUTROSPJ' | 'ORGAO';

export interface NovoVistoSolicitacao {
    tipoVisto: '90_CORRIDOS' | '90_FRACIONADOS';
    solicitante: string;
    solicitanteId: number;
    solicitanteUf: string;
    solicitanteCpfCnpj?: string;
    solicitanteCrm?: string;
    requerente?: string;
    requerenteId?: number;
    requerenteUf?: string;
    requerenteCpfCnpj?: string;
    medicoRelacionado?: string;
    medicoRelacionadoId?: number;
    medicoRelacionadoUf?: string;
    medicoRelacionadoCpfCnpj?: string;
    medicoRelacionadoCrm?: string;
    finalidade: string;
    localVisto: 'ORIGEM' | 'OUTRO_CRM';
    periodoInicio: string;
    periodoFim: string;
    ufOrigem: string;
    crmOrigem: string;
    documentoOrigem?: string;
}

@Component({
    selector: 'app-novo-visto',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, CommonModule, ReactiveFormsModule, FormsModule, InputText, Textarea, Button, RadioButton, InputMask, Select, PesquisarPessoaCustomComponent, Dialog, HeaderDivisor, CardContent, CanDirective],
    templateUrl: './novo-visto.html',
    styleUrl: './novo-visto.scss'
})
/** Cadastro de novo visto temporário. */
export class NovoVistoComponent {
    private readonly fb = inject(FormBuilder);

    protected readonly roleVistoCriar = PERMISSOES.medicos.vistoTemporario.criar;

    exibirDialogTipoVisto = true;
    tipoVistoSelecionadoTemp: '90_CORRIDOS' | '90_FRACIONADOS' = '90_CORRIDOS';
    localVistoSelecionadoTemp: 'ORIGEM' | 'OUTRO_CRM' = 'ORIGEM';

    gerarSolicitacao = output<NovoVistoSolicitacao>();
    cancelar = output<void>();

    novoVistoForm = this.fb.nonNullable.group({
        tipoVisto: this.fb.nonNullable.control<'90_CORRIDOS' | '90_FRACIONADOS'>('90_CORRIDOS'),
        solicitante: ['', Validators.required],
        requerente: [''],
        medicoRelacionado: [''],
        finalidade: ['', Validators.required]
    });

    periodoForm = this.fb.nonNullable.group({
        localVisto: this.fb.nonNullable.control<'ORIGEM' | 'OUTRO_CRM'>('ORIGEM'),
        periodoInicio: ['', Validators.required],
        periodoFim: ['', Validators.required],
        ufOrigem: ['AC', Validators.required],
        crmOrigem: ['', Validators.required],
        documentoOrigem: ['']
    });

    mostrarPesquisaSolicitante = false;
    private campoPesquisaAtivo: 'solicitante' | 'requerente' | 'medicoRelacionado' = 'solicitante';
    private solicitanteSelecionado: PessoaDomain | null = null;
    private requerenteSelecionado: PessoaDomain | null = null;
    private medicoRelacionadoSelecionado: PessoaDomain | null = null;
    readonly ufs = [
        { label: 'AC', value: 'AC' },
        { label: 'AL', value: 'AL' },
        { label: 'AP', value: 'AP' },
        { label: 'AM', value: 'AM' },
        { label: 'BA', value: 'BA' },
        { label: 'CE', value: 'CE' },
        { label: 'DF', value: 'DF' },
        { label: 'ES', value: 'ES' },
        { label: 'GO', value: 'GO' },
        { label: 'MA', value: 'MA' },
        { label: 'MT', value: 'MT' },
        { label: 'MS', value: 'MS' },
        { label: 'MG', value: 'MG' },
        { label: 'PA', value: 'PA' },
        { label: 'PB', value: 'PB' },
        { label: 'PR', value: 'PR' },
        { label: 'PE', value: 'PE' },
        { label: 'PI', value: 'PI' },
        { label: 'RJ', value: 'RJ' },
        { label: 'RN', value: 'RN' },
        { label: 'RS', value: 'RS' },
        { label: 'RO', value: 'RO' },
        { label: 'RR', value: 'RR' },
        { label: 'SC', value: 'SC' },
        { label: 'SP', value: 'SP' },
        { label: 'SE', value: 'SE' },
        { label: 'TO', value: 'TO' }
    ];

    readonly locaisVisto: Array<{ label: string; value: 'ORIGEM' | 'OUTRO_CRM' }> = [
        { label: 'Origem', value: 'ORIGEM' },
        { label: 'Outro CRM', value: 'OUTRO_CRM' }
    ];

    readonly tiposSolicitanteVistoCorridos: TipoPessoaPesquisa[] = ['OUTROSPF', 'PRESTADOR', 'OUTROSPJ'];
    readonly tiposSolicitanteVistoFracionados: TipoPessoaPesquisa[] = ['MEDICO', 'PRESTADOR', 'OUTROSPJ'];
    readonly tiposSolicitanteVistoFracionadoOutroCrm: TipoPessoaPesquisa[] = ['ORGAO'];
    readonly tiposRequerenteVistoFracionadoOutroCrm: TipoPessoaPesquisa[] = ['OUTROSPF', 'PRESTADOR', 'OUTROSPJ'];
    readonly tiposMedicoRelacionado: TipoPessoaPesquisa[] = ['MEDICO'];

    constructor() {
        this.periodoForm.controls.periodoInicio.valueChanges.subscribe(() => this.atualizarDataFimVistoCorrido());
        this.novoVistoForm.controls.tipoVisto.valueChanges.subscribe(() => this.atualizarDataFimVistoCorrido());
    }

    onGerarSolicitacao(): void {
        this.atualizarDataFimVistoCorrido();
        if (this.novoVistoForm.invalid || this.periodoForm.invalid || !this.solicitanteSelecionado?.id || !this.solicitanteSelecionado.uf) {
            this.novoVistoForm.markAllAsTouched();
            this.periodoForm.markAllAsTouched();
            return;
        }

        const solicitanteSelecionado = this.solicitanteSelecionado;
        const requerenteSelecionado = this.requerenteSelecionado;
        const medicoRelacionadoSelecionado = this.medicoRelacionadoSelecionado;
        const requerenteObrigatorio = this.isRequerenteObrigatorio();
        const medicoRelacionadoObrigatorio = this.isMedicoRelacionadoObrigatorio();
        if (
            !solicitanteSelecionado?.id ||
            !solicitanteSelecionado.uf ||
            (requerenteObrigatorio && (!requerenteSelecionado?.id || !requerenteSelecionado.uf)) ||
            (medicoRelacionadoObrigatorio && (!medicoRelacionadoSelecionado?.id || !medicoRelacionadoSelecionado.uf))
        ) {
            this.novoVistoForm.markAllAsTouched();
            return;
        }

        this.gerarSolicitacao.emit({
            ...this.novoVistoForm.getRawValue(),
            ...this.periodoForm.getRawValue(),
            solicitanteId: solicitanteSelecionado.id,
            solicitanteUf: solicitanteSelecionado.uf,
            solicitanteCpfCnpj: solicitanteSelecionado.registro,
            solicitanteCrm: solicitanteSelecionado.crm,
            requerenteId: requerenteObrigatorio ? requerenteSelecionado?.id : undefined,
            requerenteUf: requerenteObrigatorio ? requerenteSelecionado?.uf : undefined,
            requerenteCpfCnpj: requerenteObrigatorio ? requerenteSelecionado?.registro : undefined,
            medicoRelacionado: medicoRelacionadoObrigatorio ? medicoRelacionadoSelecionado?.nome : solicitanteSelecionado.nome,
            medicoRelacionadoId: medicoRelacionadoObrigatorio ? medicoRelacionadoSelecionado?.id : solicitanteSelecionado.id,
            medicoRelacionadoUf: medicoRelacionadoObrigatorio ? medicoRelacionadoSelecionado?.uf : solicitanteSelecionado.uf,
            medicoRelacionadoCpfCnpj: medicoRelacionadoObrigatorio ? medicoRelacionadoSelecionado?.registro : solicitanteSelecionado.registro,
            medicoRelacionadoCrm: medicoRelacionadoObrigatorio ? medicoRelacionadoSelecionado?.crm : solicitanteSelecionado.crm
        });
    }

    selecionarTipoVisto(tipo: '90_CORRIDOS' | '90_FRACIONADOS'): void {
        this.novoVistoForm.controls.tipoVisto.setValue(tipo);
        if (!this.isVistoFracionado()) {
            this.periodoForm.controls.localVisto.setValue('ORIGEM');
        }
        this.limparSolicitante();
        this.limparCamposCondicionais();
        this.atualizarValidadoresCamposCondicionais();
        this.atualizarDataFimVistoCorrido();
    }

    selecionarLocalVisto(local: 'ORIGEM' | 'OUTRO_CRM'): void {
        this.periodoForm.controls.localVisto.setValue(local);
        this.limparSolicitante();
        this.limparCamposCondicionais();
        this.atualizarValidadoresCamposCondicionais();
    }

    onCancelar(): void {
        this.tipoVistoSelecionadoTemp = this.novoVistoForm.controls.tipoVisto.value;
        this.localVistoSelecionadoTemp = this.periodoForm.controls.localVisto.value;
        this.exibirDialogTipoVisto = true;
    }

    confirmarDialogTipoVisto(): void {
        this.selecionarTipoVisto(this.tipoVistoSelecionadoTemp);
        if (this.tipoVistoSelecionadoTemp === '90_FRACIONADOS') {
            this.selecionarLocalVisto(this.localVistoSelecionadoTemp);
        }
        this.exibirDialogTipoVisto = false;
    }

    cancelarDialogTipoVisto(): void {
        this.exibirDialogTipoVisto = false;
        this.cancelar.emit();
    }

    abrirPesquisaSolicitante(): void {
        this.campoPesquisaAtivo = 'solicitante';
        this.mostrarPesquisaSolicitante = true;
    }

    abrirPesquisaRequerente(): void {
        if (!this.isRequerenteObrigatorio()) {
            return;
        }
        this.campoPesquisaAtivo = 'requerente';
        this.mostrarPesquisaSolicitante = true;
    }

    abrirPesquisaMedicoRelacionado(): void {
        if (!this.isMedicoRelacionadoObrigatorio()) {
            return;
        }
        this.campoPesquisaAtivo = 'medicoRelacionado';
        this.mostrarPesquisaSolicitante = true;
    }

    onDocumentoOrigemInput(): void {
        const controle = this.periodoForm.controls.documentoOrigem;
        const valorNumerico = (controle.value ?? '').replace(/\D+/g, '');
        if (controle.value !== valorNumerico) {
            controle.setValue(valorNumerico, { emitEvent: false });
        }
    }

    onSelecionarSolicitante(pessoa: PessoaDomain): void {
        const nome = (pessoa?.nome ?? '').trim();
        if (this.campoPesquisaAtivo === 'requerente') {
            this.requerenteSelecionado = pessoa;
            this.novoVistoForm.controls.requerente.setValue(nome);
        } else if (this.campoPesquisaAtivo === 'medicoRelacionado') {
            this.medicoRelacionadoSelecionado = pessoa;
            this.novoVistoForm.controls.medicoRelacionado.setValue(nome);
        } else {
            this.solicitanteSelecionado = pessoa;
            this.novoVistoForm.controls.solicitante.setValue(nome);
        }
        this.mostrarPesquisaSolicitante = false;
    }

    isRequerenteObrigatorio(): boolean {
        return this.isVistoFracionado() && this.periodoForm.controls.localVisto.value === 'OUTRO_CRM';
    }

    isMedicoRelacionadoObrigatorio(): boolean {
        return this.isOutroCrmSelecionado();
    }

    isVistoFracionado(): boolean {
        return this.novoVistoForm.controls.tipoVisto.value === '90_FRACIONADOS';
    }

    isVistoCorridos(): boolean {
        return this.novoVistoForm.controls.tipoVisto.value === '90_CORRIDOS';
    }

    isOutroCrmSelecionado(): boolean {
        return this.isVistoFracionado() && this.periodoForm.controls.localVisto.value === 'OUTRO_CRM';
    }

    isCrmOrigemVisivel(): boolean {
        return !this.isVistoFracionado() || this.isOutroCrmSelecionado();
    }

    getTiposPessoaPesquisa(): TipoPessoaPesquisa[] {
        if (this.campoPesquisaAtivo === 'medicoRelacionado') {
            return this.tiposMedicoRelacionado;
        }
        if (this.campoPesquisaAtivo === 'requerente') {
            return this.isOutroCrmSelecionado() ? this.tiposRequerenteVistoFracionadoOutroCrm : this.tiposSolicitanteVistoFracionados;
        }

        if (this.isOutroCrmSelecionado()) {
            return this.tiposSolicitanteVistoFracionadoOutroCrm;
        }
        return this.isVistoFracionado() ? this.tiposSolicitanteVistoFracionados : this.tiposSolicitanteVistoCorridos;
    }

    getRotuloUfLocalVisto(): string {
        return this.isVistoFracionado() && !this.isOutroCrmSelecionado() ? 'UF Destino' : 'UF Origem';
    }

    atualizarDataFimVistoCorrido(): void {
        if (!this.isVistoCorridos()) {
            return;
        }

        const dataInicio = this.parseDataBr(this.periodoForm.controls.periodoInicio.value);
        const periodoFim = this.periodoForm.controls.periodoFim;
        if (!dataInicio) {
            if (periodoFim.value) {
                periodoFim.setValue('', { emitEvent: false });
            }
            return;
        }

        const dataFim = new Date(dataInicio);
        dataFim.setDate(dataFim.getDate() + 90);
        const valorDataFim = this.formatarDataBr(dataFim);
        if (periodoFim.value !== valorDataFim) {
            periodoFim.setValue(valorDataFim, { emitEvent: false });
        }
    }

    private limparCamposCondicionais(): void {
        if (!this.isRequerenteObrigatorio()) {
            this.requerenteSelecionado = null;
            this.novoVistoForm.controls.requerente.setValue('');
        }
        if (!this.isMedicoRelacionadoObrigatorio()) {
            this.medicoRelacionadoSelecionado = null;
            this.novoVistoForm.controls.medicoRelacionado.setValue('');
        }
        if (!this.isOutroCrmSelecionado()) {
            this.periodoForm.controls.crmOrigem.setValue('');
            this.periodoForm.controls.documentoOrigem.setValue('');
        }
    }

    private atualizarValidadoresCamposCondicionais(): void {
        const crmOrigem = this.periodoForm.controls.crmOrigem;
        const requerente = this.novoVistoForm.controls.requerente;
        const medicoRelacionado = this.novoVistoForm.controls.medicoRelacionado;
        if (this.isCrmOrigemVisivel()) {
            crmOrigem.setValidators(Validators.required);
        } else {
            crmOrigem.clearValidators();
        }
        crmOrigem.updateValueAndValidity();

        if (this.isRequerenteObrigatorio()) {
            requerente.setValidators(Validators.required);
        } else {
            requerente.clearValidators();
        }
        requerente.updateValueAndValidity();

        if (this.isMedicoRelacionadoObrigatorio()) {
            medicoRelacionado.setValidators(Validators.required);
        } else {
            medicoRelacionado.clearValidators();
        }
        medicoRelacionado.updateValueAndValidity();
    }

    private limparSolicitante(): void {
        this.solicitanteSelecionado = null;
        this.novoVistoForm.controls.solicitante.setValue('');
    }

    private parseDataBr(valor: string): Date | null {
        const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((valor ?? '').trim());
        if (!match) {
            return null;
        }

        const dia = Number(match[1]);
        const mes = Number(match[2]);
        const ano = Number(match[3]);
        const data = new Date(ano, mes - 1, dia);
        if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) {
            return null;
        }
        return data;
    }

    private formatarDataBr(data: Date): string {
        const dia = String(data.getDate()).padStart(2, '0');
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const ano = data.getFullYear();
        return `${dia}/${mes}/${ano}`;
    }
}
